import { ArrowUp, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FlotionMark, FlotionWordmark } from "@/components/brand/FlotionLogo";
import { Button } from "@/components/ui/button";
import { HeroBackdrop } from "./HeroBackdrop";

const SUGGESTION_CHIPS = [
  "Find multifamily sites in Chicago that fit young-professional renters",
  "Score a list of retail candidates against the QSR profile",
  "Surface acquisition targets above 50 units on the North Side",
  "Screen affordable multifamily inside the Loop",
];

const UPLOAD_PROMPT = "I want to upload a CSV of addresses to score.";

interface RecentCampaign {
  id: string;
  name: string;
  goal: string | null;
  locationCount: number;
  profileName: string | null;
}

interface HeroPromptProps {
  onSubmit: (text: string) => void;
  /** Select an existing campaign from a recent-campaign card. */
  onSelectCampaign?: (id: string) => void;
  refreshKey?: number;
}

export function HeroPrompt({ onSubmit, onSelectCampaign, refreshKey }: HeroPromptProps) {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [recent, setRecent] = useState<RecentCampaign[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey re-fetches after a new campaign
  useEffect(() => {
    let mounted = true;
    fetch("/api/campaigns?limit=6")
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!mounted) return;
        const scored = ((json?.data ?? []) as RecentCampaign[])
          .filter((c) => c.locationCount > 0)
          .slice(0, 3);
        setRecent(scored);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  function handleSubmit() {
    const text = input.trim();
    if (!text) return;
    onSubmit(text);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <section className="relative flex flex-1 flex-col items-center justify-center overflow-hidden bg-background px-6">
      <HeroBackdrop />
      {/* Scrim — dims the cartographic grid so the hero text stays legible. */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/75 via-background/70 to-background/90" />

      <div className="hero-rise relative z-10 w-full max-w-2xl space-y-7">
        {/* Logo lockup */}
        <div className="flex flex-col items-center gap-3 text-center">
          <FlotionMark size={72} />
          <FlotionWordmark height={40} className="text-foreground" />
          <span className="font-mono text-xs uppercase tracking-[0.28em] text-muted-foreground">
            Know your next move
          </span>
        </div>

        <div className="space-y-3 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            What kind of locations are you screening?
          </h1>
          <p className="mx-auto max-w-xl text-muted-foreground">
            Describe a search, paste a list of addresses, or upload a CSV.{" "}
            <span className="text-primary">Atlas</span> will pick a scoring profile and start
            scoring.
          </p>
        </div>

        {/* Prompt card */}
        <div className="rounded-2xl border border-border bg-card/90 p-3 shadow-lg backdrop-blur">
          <label htmlFor="hero-prompt" className="sr-only">
            Describe the locations you want to screen
          </label>
          <textarea
            id="hero-prompt"
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Find multifamily sites near transit that fit young-professional renters…"
            rows={3}
            className="w-full resize-none bg-transparent px-2 py-1.5 text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <div className="mt-1 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
              onClick={() => onSubmit(UPLOAD_PROMPT)}
              title="Upload a list of addresses"
              aria-label="Upload a list of addresses"
            >
              <Upload className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              className="h-9 w-9 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleSubmit}
              disabled={!input.trim()}
              aria-label="Start"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Suggestion chips */}
        <div className="flex flex-wrap justify-center gap-2">
          {SUGGESTION_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => onSubmit(chip)}
              className="rounded-full border border-border bg-card/70 px-3.5 py-1.5 text-xs text-muted-foreground backdrop-blur transition-colors hover:border-primary/40 hover:text-foreground"
            >
              {chip}
            </button>
          ))}
        </div>

        {/* Recent campaigns */}
        {recent.length > 0 && onSelectCampaign && (
          <div className="space-y-2 pt-2">
            <p className="text-center font-mono text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
              Recent campaigns
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {recent.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelectCampaign(c.id)}
                  className="flex flex-col gap-1 rounded-xl border border-border bg-card/70 p-3 text-left backdrop-blur transition-colors hover:border-primary/40"
                >
                  <span className="truncate text-sm font-medium text-foreground">{c.name}</span>
                  <span className="line-clamp-2 min-h-8 text-xs text-muted-foreground">
                    {c.goal ?? " "}
                  </span>
                  <span className="mt-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    <span className="size-1.5 rounded-full bg-score-high" />
                    Scored · {c.locationCount}
                    {c.profileName ? ` · ${c.profileName}` : ""}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
